/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import defaultsDeep from 'lodash/defaultsDeep';
import isEmpty from 'lodash/isEmpty';
import compose from 'lodash/fp/compose';
import Picture from '../../ui/Picture';
import Spacer from '../../ui/Spacer';
import DotComponent from '../../ui/DotComponent';
import CtaLink from '../../ui/CtaLink';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { OffsetConsumer } from '../../../contexts/OffsetContext';
import { Container, Row, Column } from '../../../styles/Grid';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import {
  transformWildcardsToText,
  getWildcardData,
} from '../../../utils/wildcards';
import {
  imageCss,
  imageRightCss,
  headingCss,
  bodyCss,
  containerMarginCss,
  containerPaddingCss,
} from './index.styles';

const imageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 513,
    height: 384,
    gravity: 'auto:subject',
    aspectRatio: '4:3',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 597,
    height: 448,
    gravity: 'auto:subject',
    aspectRatio: '4:3',
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 326,
    height: 245,
    gravity: 'auto:subject',
    aspectRatio: '4:3',
  },
};

const defaultData = {
  BackgroundColor: { fields: { Value: { value: 'brown10' } } },
  CTA: {
    value: {
      anchor: '',
      href: '#',
      id: '',
      linktype: 'internal',
      querystring: '',
      target: '',
      text: '',
      url: '#',
    },
  },
  Description: {
    value:
      'but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letrase',
  },
  Image: {
    value: {
      alt: 'image description',
      src: '-/media/Default%20Website/cover.ashx',
    },
  },
  ImageLeft: {
    value: true,
  },
  Title: {
    value: 'There are many variations of passages',
  },
};

export class ImageWithTitleAndDescription extends SafePureComponent {
  constructor(props) {
    super(props);
    this.wildcards = getWildcardData(this.props.sitecoreContext);
  }

  /**
   * This function inject the wildcards values and transform that values to be used in the Headline and Description
   * @param {Object} fields containt the information of the current component
   * @return {React.Node} Return the headline, description and link of the component
   */
  renderHeadlineAndDescription = fields => {
    const { t } = this.props;
    const isEditing = get(this.props, 'sitecoreContext.pageEditing', false);
    const ctaHref = get(fields, 'CTA.value.href', '');
    const contextFields = get(this.props, 'sitecoreContext.route.fields', {});
    const componentName = get(this.props, 'rendering.componentName');
    const baseWildcardValuesToTransform = {
      componentName,
      contextFields,
      fields,
      t,
      wildcards: this.wildcards,
    };
    const Headline = {
      value: transformWildcardsToText({
        ...baseWildcardValuesToTransform,
        text: get(fields, 'Title.value', ''),
      }),
    };

    const Description = {
      value: transformWildcardsToText({
        ...baseWildcardValuesToTransform,
        text: get(fields, 'Description.value', ''),
      }),
    };

    return (
      <React.Fragment>
        <h2 css={headingCss}>
          <Text field={Headline} />
          <DotComponent />
        </h2>
        <p css={bodyCss}>
          <Text field={Description} />
        </p>
        {(isEditing || !isEmpty(ctaHref)) && <CtaLink cta={fields.CTA} />}
      </React.Fragment>
    );
  };

  safeRender() {
    const { props } = this;
    const fields = defaultsDeep(props.fields, defaultData);
    const imageLeft = get(fields, 'ImageLeft.value');
    const imageOffset = imageLeft ? 0 : 1;
    const textOffset = imageLeft ? 1 : 0;

    return (
      <Spacer
        backgroundColor={fields.BackgroundColor}
        marginCss={containerMarginCss}
        paddingCss={containerPaddingCss}
        containerProps={{ 'data-sticky-id': get(props, 'rendering.uid') }}
      >
        <Container>
          <Row>
            <OffsetConsumer>
              {({ offsetLeft }) => {
                const offset = offsetLeft ? 2 : 0;
                const totalWidth = offset === 2 ? 10 : 12;
                return (
                  <Column
                    cols={{ sm: 6, md: 7, lg: totalWidth }}
                    offsets={{
                      sm: 0,
                      md: 0,
                      lg: offset,
                      xl: offset,
                    }}
                  >
                    <Row>
                      <Column
                        css={!imageLeft ? imageRightCss : {}}
                        cols={{ sm: [6, 6], md: [7, 7], lg: [5, totalWidth] }}
                        offsets={{
                          sm: 0,
                          md: [imageOffset, totalWidth],
                          lg: [imageOffset, totalWidth],
                        }}
                      >
                        <Picture
                          image={fields.Image}
                          imageCss={imageCss}
                          imageSizes={imageSizes}
                        />
                      </Column>
                      <Column
                        cols={{
                          sm: [6, 6],
                          md: [7, 7],
                          lg: [offset ? 3 : 5, totalWidth],
                        }}
                        offsets={{
                          sm: 0,
                          md: [textOffset, totalWidth],
                          lg: [textOffset, totalWidth],
                        }}
                      >
                        {this.renderHeadlineAndDescription(fields)}
                      </Column>
                    </Row>
                  </Column>
                );
              }}
            </OffsetConsumer>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

ImageWithTitleAndDescription.displayName = 'ImageWithTitleAndDescription';

const ImageType = {
  value: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string,
  }),
};

const BackgroundColorType = {
  fields: PropTypes.shape({
    Value: PropTypes.shape({ value: PropTypes.string }),
  }),
};

const CtaType = {
  value: PropTypes.shape({
    anchor: PropTypes.string,
    href: PropTypes.string,
    id: PropTypes.string,
    linktype: PropTypes.string,
    querystring: PropTypes.string,
    target: PropTypes.string,
    text: PropTypes.string,
    url: PropTypes.string,
  }),
};

ImageWithTitleAndDescription.defaultProps = {
  t: key => key,
  fields: defaultData,
};

ImageWithTitleAndDescription.propTypes = {
  fields: PropTypes.shape({
    BackgroundColor: PropTypes.shape(BackgroundColorType),
    CTA: PropTypes.shape(CtaType),
    Description: PropTypes.shape({
      value: PropTypes.string,
    }),
    Image: PropTypes.shape(ImageType),
    ImageLeft: PropTypes.shape({
      value: PropTypes.bool,
    }),
    Title: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
  t: PropTypes.func,
};

export default compose(
  withSitecoreContext(),
  withNamespaces(),
)(ImageWithTitleAndDescription);
